import { FileActionButton } from "../file-action/file-action-controls";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Skeleton } from "primereact/skeleton";
import { MultiSelect } from "primereact/multiselect";
import orderInvoiceReportService from "../../services/orderInvoiceReport/orderInvoiceReportService";
import { OrderInvoiceReportFilterDTO, OrderInvoiceReportRow } from "../../models/order-invoice-report-model";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DEFAULT_PAGE_SIZE = 10;

type SortOrder = 1 | -1;
type FilterableReportField = "routeCodeName" | "orderType";

/** Format a JS Date to "YYYY-MM-DD" using local calendar — avoids UTC-shift bug on IST. */
const toLocalDateStr = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const fmtDate = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return `${String(date.getDate()).padStart(2, "0")}-${MONTHS[date.getMonth()]}-${date.getFullYear()}`;
};

const fmtMoney = (value?: number | null) => {
  if (value == null) return "-";
  return `Rs ${Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const getDeliveryLabel = (orderType?: string | null) => {
  if (orderType === "HomeDelivery") return "Home Delivery";
  if (orderType === "Couriered Order") return "Couriered";
  return orderType || "-";
};

const exportToXlsx = (rows: OrderInvoiceReportRow[]) => {
  if (!rows.length) return;

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(rows.map((row) => ({
    "Order ID": row.orderID,
    "Invoice No": row.invoiceNo,
    "Days Diff": row.orderInvoiceDateDiff,
    "Order Date": fmtDate(row.orderDate),
    "Invoiced Date": fmtDate(row.invoicedDate),
    "Customer Name": row.customerName,
    "Customer Phone": row.customerNumber,
    "Route Code": row.routeCodeName,
    "Order Created By": row.orderByName,
    "Created By Phone": row.orderByNumber,
    "Invoiced By": row.invoicedByName,
    "Invoiced By Phone": row.invoicedByNumber,
    "Order Value": row.orderValue,
    "Invoice Value": row.invoiceValue,
    "Delivery Type": getDeliveryLabel(row.orderType),
  })));

  XLSX.utils.book_append_sheet(workbook, worksheet, "Order Invoice Timeline");
  XLSX.writeFile(workbook, "OrderInvoiceTimelineReport.xlsx");
};

const OrderInvoiceReport: React.FC = () => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();

  // ── Filter input states (bound to UI inputs; not sent to API until Search is clicked) ──
  const [customerName, setCustomerName] = useState("");
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [invoiceNoSearch, setInvoiceNoSearch] = useState("");
  const [orderIdSearch, setOrderIdSearch] = useState("");
  const [invoicedBySearch, setInvoicedBySearch] = useState("");

  // ── Applied filters (copied from inputs when Search / Reset is clicked) ──
  const [appliedFilters, setAppliedFilters] = useState({
    customerName: "",
    dateFrom: null as Date | null,
    dateTo: null as Date | null,
    invoiceNoSearch: "",
    orderIdSearch: "",
    invoicedBySearch: "",
  });

  // ── Column header multi-select & sort (trigger immediate re-fetch) ──
  const [routeCodeFilter, setRouteCodeFilter] = useState<string[]>([]);
  const [deliveryTypeFilter, setDeliveryTypeFilter] = useState<string[]>([]);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(1);

  // ── Server-side pagination state ──
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [records, setRecords] = useState<OrderInvoiceReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // ── Build the API filter object from all active state ──
  const buildFilter = useCallback((): OrderInvoiceReportFilterDTO => ({
    startDate: appliedFilters.dateFrom ? toLocalDateStr(appliedFilters.dateFrom) : null,
    endDate: appliedFilters.dateTo ? toLocalDateStr(appliedFilters.dateTo) : null,
    orderByName: appliedFilters.customerName.trim() || null,
    orderID: appliedFilters.orderIdSearch.trim() ? Number(appliedFilters.orderIdSearch.trim()) : null,
    invoiceNo: appliedFilters.invoiceNoSearch.trim() ? Number(appliedFilters.invoiceNoSearch.trim()) : null,
    invoicedBy: appliedFilters.invoicedBySearch.trim() || null,
    orderType: deliveryTypeFilter.length > 0 ? deliveryTypeFilter.join(",") : null,
    routeCodeName: routeCodeFilter.length > 0 ? routeCodeFilter.join(",") : null,
    pageNumber,
    pageSize,
    sortBy: sortField || null,
    sortOrder: sortField ? (sortOrder === 1 ? "asc" : "desc") : null,
  }), [appliedFilters, deliveryTypeFilter, routeCodeFilter, pageNumber, pageSize, sortField, sortOrder]);

  const fetchReport = useCallback(async (filter: OrderInvoiceReportFilterDTO) => {
    setLoading(true);
    try {
      const response = await orderInvoiceReportService.getReport(filter);
      setRecords(response.data ?? []);
      setTotalRecords(response.totalRecords ?? 0);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message ?? error?.message ?? "Failed to load report",
        life: 4000,
      });
      setRecords([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Single reactive effect — fires whenever pagination, column filters, sort, or applied search changes
  useEffect(() => {
    fetchReport(buildFilter());
  }, [pageNumber, pageSize, routeCodeFilter, deliveryTypeFilter, sortField, sortOrder, appliedFilters, fetchReport, buildFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Column-filter option lists are derived from the current page results
  const getFieldText = (row: OrderInvoiceReportRow, field: FilterableReportField) => {
    const value = row[field];
    return value == null ? "" : String(value);
  };

  const matchesSelectedValues = (
    row: OrderInvoiceReportRow,
    field: FilterableReportField,
    selectedValues: string[]
  ) => selectedValues.length === 0 || selectedValues.includes(getFieldText(row, field));

  const currentColumnFilters = useMemo(() => ({
    routeCodeName: routeCodeFilter.length > 0 ? routeCodeFilter : undefined,
    orderType: deliveryTypeFilter.length > 0 ? deliveryTypeFilter : undefined,
  }), [deliveryTypeFilter, routeCodeFilter]);

  const getUniqueValues = (
    field: FilterableReportField,
    currentFilters: Partial<Record<FilterableReportField, string[]>>
  ) => {
    let baseRows = records;

    (Object.keys(currentFilters) as FilterableReportField[]).forEach((filterField) => {
      const selectedValues = currentFilters[filterField];
      if (selectedValues && filterField !== field) {
        baseRows = baseRows.filter((row) => matchesSelectedValues(row, filterField, selectedValues));
      }
    });

    return Array.from(new Set(baseRows.map((row) => getFieldText(row, field)).filter(Boolean))).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
    );
  };

  const routeCodeOptions = useMemo(() => getUniqueValues("routeCodeName", currentColumnFilters), [records, currentColumnFilters]);
  const deliveryTypeOptions = useMemo(() => getUniqueValues("orderType", currentColumnFilters), [records, currentColumnFilters]);

  // With server-side pagination every row returned is already the correct page — no client filter needed
  const filteredRows = records;

  const handleSearch = () => {
    setPageNumber(1);
    setAppliedFilters({ customerName, dateFrom, dateTo, invoiceNoSearch, orderIdSearch, invoicedBySearch });
  };

  const handleReset = () => {
    setCustomerName("");
    setDateFrom(null);
    setDateTo(null);
    setInvoiceNoSearch("");
    setOrderIdSearch("");
    setInvoicedBySearch("");
    setRouteCodeFilter([]);
    setDeliveryTypeFilter([]);
    setSortField(null);
    setSortOrder(1);
    setPageSize(DEFAULT_PAGE_SIZE);
    setPageNumber(1);
    // Resetting appliedFilters triggers the useEffect which re-fetches
    setAppliedFilters({ customerName: "", dateFrom: null, dateTo: null, invoiceNoSearch: "", orderIdSearch: "", invoicedBySearch: "" });
  };

  const handlePage = (event: any) => {
    setPageNumber(Math.floor(event.first / event.rows) + 1);
    setPageSize(event.rows);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder((current) => current === 1 ? -1 : 1);
    } else {
      setSortField(field);
      setSortOrder(1);
    }
    setPageNumber(1);
  };

  const setColumnFilter = (setter: React.Dispatch<React.SetStateAction<string[]>>) => (value: string[]) => {
    setPageNumber(1);
    setter(value);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      // Fetch ALL matching records (bypass pagination) for the export
      const exportFilter: OrderInvoiceReportFilterDTO = {
        ...buildFilter(),
        pageNumber: 1,
        pageSize: totalRecords || 99999,
      };
      const response = await orderInvoiceReportService.getReport(exportFilter);
      const allRows = response.data ?? [];
      if (allRows.length === 0) {
        toast.current?.show({ severity: "warn", summary: "No Data", detail: "No records to export", life: 3000 });
        return;
      }
      exportToXlsx(allRows);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Export Failed",
        detail: error?.message ?? "Could not export data",
        life: 4000,
      });
    } finally {
      setExporting(false);
    }
  };

  const clearColumnFilters = () => {
    setRouteCodeFilter([]);
    setDeliveryTypeFilter([]);
    setSortField(null);
    setSortOrder(1);
    setPageNumber(1);
  };

  const hasActiveColumnFilters =
    routeCodeFilter.length > 0 ||
    deliveryTypeFilter.length > 0 ||
    Boolean(sortField);

  const FilterableColumnHeader = ({
    title,
    filterValue,
    onFilterChange,
    options,
    sortField: columnField,
    showSort = true,
  }: {
    title: string;
    filterValue: string[];
    onFilterChange: (value: string[]) => void;
    options: string[];
    sortField: string;
    showSort?: boolean;
  }) => {
    const activeFilterCount = filterValue.length;
    const isCurrentlySorted = sortField === columnField;

    return (
      <div className="flex align-items-center gap-2" style={{ whiteSpace: "nowrap" }}>
        <span className="font-semibold">{title}</span>
        <div className="flex align-items-center gap-1">
          <MultiSelect
            value={filterValue}
            options={options}
            onChange={(event) => onFilterChange(event.value)}
            placeholder={`Filter ${title}`}
            maxSelectedLabels={0}
            selectedItemsLabel={`${activeFilterCount} selected`}
            className="oitr-custom-multiselect"
            panelStyle={{ minWidth: "210px" }}
            display="chip"
            onClick={(event) => event.stopPropagation()}
            pt={{
              root: {
                style: {
                  width: "40px",
                  height: "32px",
                  border: activeFilterCount > 0 ? "2px solid #22c55e" : "1px solid #d1d5db",
                  borderRadius: "6px",
                  background: activeFilterCount > 0 ? "#f0fdf4" : "white",
                },
              },
              trigger: {
                style: {
                  width: "100%",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: activeFilterCount > 0 ? "#22c55e" : "#6b7280",
                },
              },
              label: { style: { display: "none" } },
            }}
          />
          {activeFilterCount > 0 && <span className="oitr-filter-count">{activeFilterCount}</span>}
          {showSort && (
            <Button
              icon={isCurrentlySorted && sortOrder === -1 ? "pi pi-sort-amount-down" : "pi pi-sort-amount-up"}
              className="p-button-text p-button-sm"
              onClick={(event) => { event.stopPropagation(); handleSort(columnField); }}
              style={{
                color: isCurrentlySorted ? "#22c55e" : "#6b7280",
                padding: "4px 8px",
                minWidth: "auto",
              }}
              tooltip={`Sort ${isCurrentlySorted ? (sortOrder === 1 ? "Descending" : "Ascending") : "Ascending"}`}
              tooltipOptions={{ position: "top" }}
            />
          )}
        </div>
      </div>
    );
  };

  const SortableColumnHeader = ({ title, field }: { title: string; field: string }) => {
    const isCurrentlySorted = sortField === field;

    return (
      <div className="flex align-items-center gap-2" style={{ whiteSpace: "nowrap" }}>
        <span className="font-semibold">{title}</span>
        <Button
          icon={isCurrentlySorted && sortOrder === -1 ? "pi pi-sort-amount-down" : "pi pi-sort-amount-up"}
          className="p-button-text p-button-sm"
          onClick={(event) => { event.stopPropagation(); handleSort(field); }}
          style={{
            color: isCurrentlySorted ? "#22c55e" : "#6b7280",
            padding: "4px 8px",
            minWidth: "auto",
          }}
          tooltip={`Sort ${isCurrentlySorted ? (sortOrder === 1 ? "Descending" : "Ascending") : "Ascending"}`}
          tooltipOptions={{ position: "top" }}
        />
      </div>
    );
  };

  const orderIdBody = (row: OrderInvoiceReportRow) => (
    <span style={{ background: "#e8f4fd", color: "#0c5460", fontWeight: 700, fontSize: 12, borderRadius: 6, padding: "3px 8px" }}>
      #{row.orderID ?? "-"}
    </span>
  );

  const invoiceNoBody = (row: OrderInvoiceReportRow) => (
    <span style={{ background: "#f0faf3", color: "#155724", fontWeight: 600, fontSize: 12, borderRadius: 6, padding: "3px 8px" }}>
      {row.invoiceNo ?? "-"}
    </span>
  );

  const daysDiffBody = (row: OrderInvoiceReportRow) => {
    const days = row.orderInvoiceDateDiff;
    return days == null ? <span>-</span> : <Tag value={String(days)} severity={days <= 1 ? "success" : days <= 3 ? "warning" : "danger"} rounded />;
  };

  const customerBody = (row: OrderInvoiceReportRow) => (
    <div>
      <div style={{ fontWeight: 600, color: "#212529" }}>{row.customerName || "-"}</div>
      {row.customerNumber && <div style={{ fontSize: "11.5px", color: "#6c757d", marginTop: 2 }}>{row.customerNumber}</div>}
    </div>
  );

  const routeCodeBody = (row: OrderInvoiceReportRow) => (
    <span style={{ fontSize: 12.5, color: "#495057" }}>{row.routeCodeName || "-"}</span>
  );

  const orderByBody = (row: OrderInvoiceReportRow) => (
    <div>
      <div style={{ fontWeight: 600, color: "#212529" }}>{row.orderByName || "-"}</div>
      {row.orderByNumber && <div style={{ fontSize: "11.5px", color: "#6c757d", marginTop: 2 }}>{row.orderByNumber}</div>}
    </div>
  );

  const invoicedByBody = (row: OrderInvoiceReportRow) => (
    <div>
      <div style={{ fontWeight: 600, color: "#212529" }}>{row.invoicedByName || "-"}</div>
      {row.invoicedByNumber && <div style={{ fontSize: "11.5px", color: "#6c757d", marginTop: 2 }}>{row.invoicedByNumber}</div>}
    </div>
  );

  const orderValueBody = (row: OrderInvoiceReportRow) => (
    <span style={{ fontWeight: 600, color: "#1e3a2f" }}>{fmtMoney(row.orderValue)}</span>
  );

  const invoiceValueBody = (row: OrderInvoiceReportRow) => (
    <span style={{ fontWeight: 600, color: "#1e3a2f" }}>{fmtMoney(row.invoiceValue)}</span>
  );

  const orderTypeBody = (row: OrderInvoiceReportRow) => (
    <Tag
      value={getDeliveryLabel(row.orderType)}
      severity={row.orderType === "HomeDelivery" ? "info" : "warning"}
      rounded
    />
  );


  // ── Tracking column body ──────────────────────────────────────────────────
  const trackingBody = (row: OrderInvoiceReportRow) => (
    <Button
      id={`oitr-track-btn-${row.orderID}`}
      icon="pi pi-truck"
      className="oitr-track-btn"
      tooltip="View Delivery Tracking"
      tooltipOptions={{ position: "top" }}
      onClick={() => navigate(`/OrderDeliverySummary?orderID=${row.orderID}`)}
    />
  );

  const skeletonRows = Array.from({ length: 8 });

  return (
    <>
      <Toast ref={toast} />
      <style>{`
        .oitr-header-card{background:linear-gradient(135deg,#f8fffe,#e8f5e9);border-radius:16px;padding:16px 22px;box-shadow:0 2px 8px rgba(0,0,0,.04);border:1px solid rgba(40,167,69,.12);margin-bottom:14px;display:flex;align-items:center;justify-content:space-between;gap:14px;}
        .oitr-header-left{display:flex;align-items:center;gap:12px;}
        .oitr-icon-box{width:44px;height:44px;background:linear-gradient(135deg,#28a745,#1e8a38);border-radius:11px;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 14px rgba(40,167,69,.35);flex-shrink:0;}
        .oitr-header-card h1{font-size:18px;font-weight:700;color:#10492a;line-height:1.2;margin:0;}
        .oitr-header-card p{font-size:12px;color:#6c757d;margin:2px 0 0;}
        .oitr-export-btn{height:34px!important;border:none!important;background:linear-gradient(135deg,#1a7a34,#145c28)!important;border-radius:8px!important;font-weight:600!important;font-size:12.5px!important;color:#fff!important;box-shadow:0 2px 8px rgba(26,122,52,.35)!important;padding:0 14px!important;white-space:nowrap;}
        .oitr-export-btn:hover{background:linear-gradient(135deg,#145c28,#0e4520)!important;}
        .oitr-filter-card{background:#fff;border-radius:12px;border:1px solid rgba(40,167,69,.15);box-shadow:0 1px 4px rgba(0,0,0,.05);padding:14px 20px;margin-bottom:14px;}
        .oitr-filter-header{display:flex;align-items:center;gap:7px;margin-bottom:14px;padding-bottom:10px;border-bottom:1px solid #f0f0f0;}
        .oitr-filter-header .pi{color:#28a745;font-size:13px;}
        .oitr-filter-header span{font-weight:600;color:#374151;font-size:12.5px;}
        .oitr-filter-grid{display:grid;grid-template-columns:repeat(6,minmax(125px,1fr)) auto;gap:14px 18px;align-items:end;}
        .oitr-field{display:flex;flex-direction:column;gap:5px;}
        .oitr-field label{font-size:11px;font-weight:600;color:#6c757d;text-transform:uppercase;letter-spacing:.4px;white-space:nowrap;}
        .oitr-field .p-inputtext{height:32px!important;font-size:12.5px!important;border-radius:7px!important;border-color:#dee2e6!important;padding:0 9px!important;width:100%!important;}
        .oitr-field .p-calendar{width:100%!important;}
        .oitr-field .p-calendar .p-inputtext{width:100%!important;}
        .oitr-field .p-calendar .p-button{height:32px!important;padding:0!important;width:30px!important;}
        .oitr-btn-group{display:flex;gap:8px;align-items:flex-end;}
        .oitr-search-btn{height:32px!important;border:none!important;background:#28a745!important;border-radius:7px!important;font-weight:600!important;font-size:12.5px!important;box-shadow:0 2px 8px rgba(40,167,69,.3)!important;padding:0 14px!important;}
        .oitr-search-btn:hover{background:#1e8a38!important;}
        .oitr-reset-btn,.oitr-clear-btn{height:32px!important;background:#f8f9fa!important;border:1px solid #dee2e6!important;color:#495057!important;border-radius:7px!important;font-weight:600!important;font-size:12.5px!important;padding:0 14px!important;}
        .oitr-reset-btn:hover,.oitr-clear-btn:hover{background:#e9ecef!important;}
        .oitr-table-card{background:#fff;border-radius:12px;border:1px solid #e9ecef;box-shadow:0 1px 4px rgba(0,0,0,.05);overflow:hidden;}
        .oitr-table-actions{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 12px;border-bottom:1px solid #f1f3f5;}
        .oitr-table-count{font-size:12.5px;color:#6c757d;font-weight:600;}
        .oitr-table-card .p-datatable .p-datatable-thead>tr>th{background:#f8f9fa!important;border-bottom:2px solid #28a745!important;font-size:11px!important;font-weight:700!important;color:#374151!important;text-transform:uppercase;letter-spacing:.4px;}
        .oitr-table-card .p-datatable .p-datatable-thead>tr>th:hover{background:#f0faf3!important;}
        .oitr-table-card .p-datatable .p-datatable-tbody>tr:hover>td{background:#f0faf3!important;}
        .oitr-table-card .p-datatable .p-datatable-tbody>tr>td{font-size:12.5px;color:#495057;padding:8px 12px!important;}
        .oitr-table-card .p-paginator{border-top:1px solid #f1f3f5;background:#fff;padding:6px 12px;justify-content:center;}
        .oitr-table-card .p-paginator .p-paginator-pages .p-paginator-page.p-highlight{background:#28a745!important;border-color:#28a745!important;color:#fff!important;}
        .oitr-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:50px 20px;gap:8px;color:#adb5bd;}
        .oitr-filter-count{background:#22c55e;color:white;border-radius:10px;padding:0 5px;font-size:.65rem;font-weight:700;min-width:16px;text-align:center;line-height:16px;margin-left:-6px;}
        .oitr-table-card .p-multiselect-panel .p-multiselect-header{background:#f0fdf4;border-bottom:1px solid #bbf7d0;}
        .oitr-table-card .p-multiselect-panel .p-multiselect-item.p-highlight{background:#dcfce7;color:#166534;}
          /* Tracking button */
        .oitr-track-btn {
          width:30px !important; height:30px !important;
          border:none !important; border-radius:7px !important;
          background:#28a745 !important;
          color:#fff !important; padding:0 !important;
          box-shadow:0 2px 8px rgba(40,167,69,.3) !important;
          transition:background .15s ease !important;
          display:inline-flex !important; align-items:center !important; justify-content:center !important;
        }
        .oitr-track-btn:hover { background:#1e8a38 !important; }
        .oitr-track-btn .p-button-icon { font-size:13px !important; }

        @media(max-width:1300px){.oitr-filter-grid{grid-template-columns:repeat(3,minmax(150px,1fr));}}
        @media(max-width:900px){.oitr-filter-grid{grid-template-columns:repeat(2,1fr);}.oitr-table-actions{align-items:flex-start;flex-direction:column;}}
        @media(max-width:550px){.oitr-filter-grid{grid-template-columns:1fr;}.oitr-header-card{flex-direction:column;align-items:flex-start;}}
      `}</style>

      <div className="layout-content mt-3 mb-3">
        <div className="oitr-header-card">
          <div className="oitr-header-left">
            <div className="oitr-icon-box"><i className="pi pi-file" style={{ color: "#fff", fontSize: 20 }} /></div>
            <div>
              <h1>Order Invoice Timeline Report</h1>
              <p>Track and analyze orders with their invoice details and processing timelines</p>
            </div>
          </div>
          <FileActionButton action="export"
            id="oitr-export-btn"
            label="Export"
            icon="pi pi-file-excel"
            className="oitr-export-btn"
            onClick={handleExport}
            loading={exporting}
            disabled={loading || filteredRows.length === 0}
          />
        </div>

        <div className="oitr-filter-card">
          <div className="oitr-filter-header">
            <i className="pi pi-filter-fill" />
            <span>Filter Records</span>
          </div>
          <div className="oitr-filter-grid">
            <div className="oitr-field">
              <label htmlFor="oitr-cname">Customer Name</label>
              <InputText
                id="oitr-cname"
                value={customerName}
                onChange={(event) => setCustomerName(event.target.value)}
                placeholder="Enter name..."
                onKeyDown={(event) => event.key === "Enter" && handleSearch()}
              />
            </div>
            <div className="oitr-field">
              <label htmlFor="oitr-dfrom">Order Date From</label>
              <Calendar
                inputId="oitr-dfrom"
                value={dateFrom}
                onChange={(event) => setDateFrom(event.value as Date | null)}
                showIcon
                dateFormat="dd/mm/yy"
                placeholder="From"
              />
            </div>
            <div className="oitr-field">
              <label htmlFor="oitr-dto">Order Date To</label>
              <Calendar
                inputId="oitr-dto"
                value={dateTo}
                onChange={(event) => setDateTo(event.value as Date | null)}
                showIcon
                dateFormat="dd/mm/yy"
                placeholder="To"
              />
            </div>
            <div className="oitr-field">
              <label htmlFor="oitr-order-id">Order ID</label>
              <InputText
                id="oitr-order-id"
                value={orderIdSearch}
                onChange={(event) => setOrderIdSearch(event.target.value.replace(/\D/g, ""))}
                placeholder="Search order..."
                onKeyDown={(event) => event.key === "Enter" && handleSearch()}
              />
            </div>
            <div className="oitr-field">
              <label htmlFor="oitr-invoice-no">Invoice No</label>
              <InputText
                id="oitr-invoice-no"
                value={invoiceNoSearch}
                onChange={(event) => setInvoiceNoSearch(event.target.value.replace(/\D/g, ""))}
                placeholder="Search invoice..."
                onKeyDown={(event) => event.key === "Enter" && handleSearch()}
              />
            </div>
            <div className="oitr-field">
              <label htmlFor="oitr-invoiced-by">Invoiced By</label>
              <InputText
                id="oitr-invoiced-by"
                value={invoicedBySearch}
                onChange={(event) => setInvoicedBySearch(event.target.value)}
                placeholder="Search name/phone..."
                onKeyDown={(event) => event.key === "Enter" && handleSearch()}
              />
            </div>
            <div className="oitr-btn-group">
              <Button id="oitr-search-btn" label="Search" icon="pi pi-search" className="oitr-search-btn" onClick={handleSearch} loading={loading} />
              <Button id="oitr-reset-btn" label="Reset" icon="pi pi-refresh" className="oitr-reset-btn" onClick={handleReset} disabled={loading} />
            </div>
          </div>
        </div>

        <div className="oitr-table-card">
          {loading ? (
            <div className="p-3">{skeletonRows.map((_, index) => <Skeleton key={index} height="2rem" className="mb-2" />)}</div>
          ) : records.length === 0 ? (
            <div className="oitr-empty"><i className="pi pi-inbox" style={{ fontSize: 46 }} /><p>No records found</p></div>
          ) : (
            <>
              <div className="oitr-table-actions">
                <span className="oitr-table-count">
                  Showing {totalRecords > 0 ? (pageNumber - 1) * pageSize + 1 : 0}–{Math.min(pageNumber * pageSize, totalRecords)} of {totalRecords} records
                </span>
                {hasActiveColumnFilters && (
                  <Button
                    label="Clear Column Filters"
                    icon="pi pi-filter-slash"
                    className="oitr-clear-btn"
                    onClick={clearColumnFilters}
                  />
                )}
              </div>
              <DataTable
                value={filteredRows}
                lazy
                paginator
                rows={pageSize}
                first={(pageNumber - 1) * pageSize}
                totalRecords={totalRecords}
                onPage={handlePage}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                currentPageReportTemplate="{first} to {last} of {totalRecords}"
                responsiveLayout="scroll"
                scrollable
                stripedRows
                showGridlines={false}
                emptyMessage="No records found."
              >
                <Column
                  field="orderID"
                  header={<SortableColumnHeader title="Order ID" field="orderID" />}
                  body={orderIdBody}
                  style={{ minWidth: 120 }}
                />
                <Column
                  field="invoiceNo"
                  header={<SortableColumnHeader title="Invoice No" field="invoiceNo" />}
                  body={invoiceNoBody}
                  style={{ minWidth: 130 }}
                />
                <Column field="orderInvoiceDateDiff" header={<SortableColumnHeader title="Days Diff" field="orderInvoiceDateDiff" />} body={daysDiffBody} style={{ minWidth: 105 }} />
                <Column field="orderDate" header={<SortableColumnHeader title="Order Date" field="orderDate" />} body={(row) => fmtDate(row.orderDate)} style={{ minWidth: 120 }} />
                <Column field="invoicedDate" header={<SortableColumnHeader title="Invoiced Date" field="invoicedDate" />} body={(row) => fmtDate(row.invoicedDate)} style={{ minWidth: 130 }} />
                <Column field="customerName" header={<SortableColumnHeader title="Customer Name" field="customerName" />} body={customerBody} style={{ minWidth: 165 }} />
                <Column
                  field="routeCodeName"
                  header={<FilterableColumnHeader title="Route Code" filterValue={routeCodeFilter} onFilterChange={setColumnFilter(setRouteCodeFilter)} options={routeCodeOptions} sortField="routeCodeName" showSort={false} />}
                  body={routeCodeBody}
                  style={{ minWidth: 145 }}
                />
                <Column field="orderByName" header={<SortableColumnHeader title="Order Created By" field="orderByName" />} body={orderByBody} style={{ minWidth: 165 }} />
                <Column field="invoicedByName" header={<SortableColumnHeader title="Invoiced By" field="invoicedByName" />} body={invoicedByBody} style={{ minWidth: 155 }} />
                <Column field="orderValue" header={<SortableColumnHeader title="Order Value" field="orderValue" />} body={orderValueBody} style={{ minWidth: 120 }} />
                <Column field="invoiceValue" header={<SortableColumnHeader title="Invoice Value" field="invoiceValue" />} body={invoiceValueBody} style={{ minWidth: 125 }} />
                <Column
                  field="orderType"
                  header={<FilterableColumnHeader title="Delivery Type" filterValue={deliveryTypeFilter} onFilterChange={setColumnFilter(setDeliveryTypeFilter)} options={deliveryTypeOptions} sortField="orderType" showSort={false} />}
                  body={orderTypeBody}
                  style={{ minWidth: 150 }}
                />
                <Column header="Tracking" body={trackingBody} style={{ minWidth: "80px", textAlign: "center" }} headerStyle={{ textAlign: "center" }} />
              </DataTable>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default OrderInvoiceReport;